// Pure model for the learning-progress overview: status of each person, filtering,
// sorting and the spreadsheet (CSV) export. Self-study state only: nothing here is a
// training record, an assessment or a competence.
import type { LearningOverview, LearningProgressState } from '@/domain/types'
import { catalogueEntry } from '@/features/demo/personas'
import { tutorialContent, type TutorialContent } from '@/features/learning'
import { getRoleSteps } from '@/features/learning/progress'

export type OverviewPerson = LearningOverview['people'][number]
export type LearningStatus = 'not_started' | 'started' | 'finished' | 'older_only'

export const STATUS_LABEL: Record<LearningStatus, string> = {
  not_started: 'Not started',
  started: 'Started',
  finished: 'Finished',
  older_only: 'Needs the new edition',
}

/** What "finished" means on screen and in the export. */
export const FINISHED_MEANS = "Finished means every step on the person's chosen path has been read."

/** The figures shown for one person's progress on one content edition. */
export function progressFigures(progress: LearningProgressState | null | undefined, content: TutorialContent = tutorialContent) {
  const role = progress?.selectedRole ?? 'ALL'
  const path = getRoleSteps(content, role)
  const pathIds = new Set(path.map((s) => s.id))
  const read = (progress?.readStepIds ?? []).filter((id) => pathIds.has(id)).length
  const practised = (progress?.practisedStepIds ?? []).filter((id) => pathIds.has(id)).length
  const pathLabel = content.roles.find((r) => r.code === role)?.label ?? role
  const current = content.journey.steps.find((s) => s.id === progress?.currentStepId)?.title ?? null
  const started = Boolean(progress && (progress.hasStarted || read > 0 || practised > 0))
  return { role, pathLabel, pathLength: path.length, read, practised, current, started, finished: path.length > 0 && read === path.length }
}

/** "2 of 4 steps read on the Permit Holder path" (or the whole journey). */
export function progressSentence(progress: LearningProgressState | null | undefined, content: TutorialContent = tutorialContent): string {
  const f = progressFigures(progress, content)
  if (!f.started) return 'Not started yet.'
  const path = f.role === 'ALL' ? 'the whole journey' : `the ${f.pathLabel} path`
  return `${f.read} of ${f.pathLength} steps read on ${path}, ${f.practised} ${f.practised === 1 ? 'action' : 'actions'} practised.`
}

export function overviewRow(p: OverviewPerson, content: TutorialContent = tutorialContent) {
  const f = progressFigures(p.progress, content)
  const status: LearningStatus = f.finished ? 'finished' : f.started ? 'started' : p.older_edition ? 'older_only' : 'not_started'
  const persona = catalogueEntry(p.persona_code)
  return { ...f, status, personaRole: persona?.role ?? null, roles: p.roles ?? [] }
}
export type OverviewRow = ReturnType<typeof overviewRow>

export type OverviewFilter = { role: string; status: LearningStatus | '' }
export type OverviewSort = 'activity' | 'name' | 'progress'
export const NO_ROLE = '-'

/** Role codes present in the list, for the role filter ('-' = no PTW role). */
export function roleOptions(people: OverviewPerson[]): string[] {
  const codes = new Set<string>()
  let none = false
  for (const p of people) {
    if (!p.roles || p.roles.length === 0) none = true
    for (const r of p.roles ?? []) codes.add(r)
  }
  return [...codes, ...(none ? [NO_ROLE] : [])]
}

export function applyOverview(people: OverviewPerson[], filter: OverviewFilter, sort: OverviewSort, content: TutorialContent = tutorialContent) {
  const rows = people.map((p) => ({ p, r: overviewRow(p, content) }))
  const kept = rows.filter(({ p, r }) => {
    if (filter.status && r.status !== filter.status) return false
    if (filter.role === NO_ROLE) return (p.roles ?? []).length === 0
    if (filter.role && !(p.roles ?? []).includes(filter.role)) return false
    return true
  })
  const time = (iso: string | null | undefined) => (iso ? new Date(iso).getTime() : -Infinity)
  const byName = (a: (typeof rows)[number], b: (typeof rows)[number]) => a.p.display_name.localeCompare(b.p.display_name)
  return kept.sort((a, b) => {
    if (sort === 'name') return byName(a, b)
    if (sort === 'progress') {
      const pa = a.r.pathLength ? a.r.read / a.r.pathLength : 0
      const pb = b.r.pathLength ? b.r.read / b.r.pathLength : 0
      return pb - pa || b.r.practised - a.r.practised || byName(a, b)
    }
    return time(b.p.updated_at) - time(a.p.updated_at) || byName(a, b)
  })
}

/** One spreadsheet cell: quoted, and never read as a formula (CSV injection). */
export function csvCell(value: unknown): string {
  let s = value === null || value === undefined ? '' : String(value)
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`
  return `"${s.replace(/"/g, '""')}"`
}

export type CsvMeta = { contentVersion: string; generatedAt: string; exportedBy: string; synthetic: boolean; filterNote: string }

/**
 * The overview as CSV. The first rows say what the file is (self-study, not a training
 * record) so the statement travels with the data; then one header row and one row per
 * person, in the order shown on screen.
 */
export function overviewCsv(rows: ReturnType<typeof applyOverview>, meta: CsvMeta, fmt: (iso: string | null | undefined) => string): string {
  const lines: string[] = []
  const note = (text: string) => lines.push(csvCell(text))
  // An empty date stays an empty cell (the app's "-" for no value would read as text).
  const when = (iso: string | null | undefined) => (iso ? fmt(iso) : '')
  note(
    `${meta.synthetic ? 'SYNTHETIC DEMONSTRATION - fictitious people. ' : ''}BadrEOR PTW Help & Learning: self-study progress. ` +
      'Not a training record, an assessment or proof of competence; it grants no role, licence or authority to work.',
  )
  note(`Tutorial content ${meta.contentVersion} (draft pending HSE review). Generated ${fmt(meta.generatedAt)} by ${meta.exportedBy}. ${meta.filterNote}`)
  note(FINISHED_MEANS)
  lines.push('')
  const header = [
    'Person', 'Organisation', 'PTW roles', 'Status', 'Path', 'Steps read', 'Steps on path', 'Actions practised',
    'Current step', 'Started', 'Last activity', 'Older edition', 'Older edition last activity', 'Older edition steps read',
  ]
  lines.push(header.map(csvCell).join(','))
  for (const { p, r } of rows) {
    lines.push(
      [
        p.display_name,
        p.organisation ?? '',
        r.roles.join(' '),
        STATUS_LABEL[r.status],
        r.started ? r.pathLabel : '',
        r.started ? r.read : '',
        r.started ? r.pathLength : '',
        r.started ? r.practised : '',
        r.started ? (r.current ?? '') : '',
        when(p.started_at),
        when(p.updated_at),
        p.older_edition?.content_version ?? '',
        when(p.older_edition?.updated_at),
        p.older_edition?.read_count ?? '',
      ]
        .map(csvCell)
        .join(','),
    )
  }
  return `﻿${lines.join('\r\n')}\r\n`
}
